import { useState } from "react"
import { Minus, Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import type { GroupedAddOn } from "@/lib/addon-groups"
import { AddOnNoteMedia } from "./AddOnNoteMedia"

interface GroupedAddonModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  grouped: GroupedAddOn
  selectedAddOns: Record<string, number>
  maxTotal: number | null
  onApply: (quantities: Record<string, number>) => void
  contextNote?: string
}

export function GroupedAddonModal({
  open,
  onOpenChange,
  grouped,
  selectedAddOns,
  maxTotal,
  onApply,
  contextNote,
}: GroupedAddonModalProps) {
  const [quantities, setQuantities] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {}
    for (const child of grouped.children) {
      initial[child.id] = selectedAddOns[child.id] ?? 0
    }
    return initial
  })

  const totalQuantity = Object.values(quantities).reduce(
    (sum, qty) => sum + qty,
    0
  )

  function setChildQuantity(childId: string, qty: number) {
    const next = { ...quantities, [childId]: Math.max(0, qty) }
    const nextTotal = Object.values(next).reduce(
      (sum, q) => sum + q,
      0
    )
    if (maxTotal !== null && nextTotal > maxTotal) return
    setQuantities(next)
  }

  function handleApply() {
    onApply(quantities)
    onOpenChange(false)
  }

  function handleClear() {
    const cleared: Record<string, number> = {}
    for (const child of grouped.children) {
      cleared[child.id] = 0
    }
    onApply(cleared)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!flex !flex-col !max-h-[min(90dvh,640px)] !w-[min(calc(100vw-1rem),28rem)] !max-w-[min(calc(100vw-1rem),28rem)] !gap-0 !overflow-hidden border-border bg-card !p-0">
        <DialogHeader className="shrink-0 border-b border-border px-5 pt-5 pb-4 sm:px-6">
          <DialogTitle className="text-xl font-bold tracking-wide uppercase leading-snug break-words pr-8">
            {grouped.name}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Choose your preferred option{grouped.children.length > 1 ? "s" : ""} and quantity{maxTotal !== null ? `. Total across all options cannot exceed ${maxTotal}` : ""}.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
          {contextNote && (
            <div
              className="mx-5 mt-5 flex items-start gap-2.5 rounded-xl border border-primary/30 px-4 py-3 sm:mx-6"
              style={{ background: "color-mix(in srgb, var(--primary) 8%, transparent)" }}
            >
              <span className="mt-px text-base leading-none text-primary">ℹ</span>
              <p className="text-[13px] font-medium leading-snug text-foreground">{contextNote}</p>
            </div>
          )}

          <div className="flex flex-col gap-3 px-5 py-5 sm:px-6">
            {grouped.children.map((child) => {
              const qty = quantities[child.id] ?? 0
              const maxForChild = maxTotal !== null ? maxTotal - totalQuantity + qty : 99

              return (
                <div
                  key={child.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-border p-3"
                >
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <AddOnNoteMedia
                      note={child.note ?? ""}
                      label={child.name}
                      className="text-sm font-medium text-foreground leading-snug break-words"
                    />
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setChildQuantity(child.id, qty - 1)}
                      disabled={qty <= 0}
                      className="size-7 rounded-full border border-border bg-muted/50 text-muted-foreground hover:bg-muted"
                    >
                      <Minus className="size-3.5" />
                    </Button>

                    <span className="min-w-6 text-center text-sm font-bold text-foreground">
                      {qty}
                    </span>

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setChildQuantity(child.id, qty + 1)}
                      disabled={qty >= maxForChild}
                      className="size-7 rounded-full"
                    >
                      <Plus className="size-3.5" />
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="shrink-0 flex gap-3 border-t border-border bg-muted/30 px-5 py-4 sm:px-6">
          {totalQuantity > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={handleClear}
              size="xl"
              className="flex-1"
            >
              Clear all
            </Button>
          )}
          <Button
            type="button"
            onClick={handleApply}
            size="xl"
            className="flex-1"
          >
            {totalQuantity > 0 ? `Confirm (${totalQuantity})` : "Confirm"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
